import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { RangeCustomEvent } from '@ionic/angular';

interface RgbValue {
  r: number,
  g: number,
  b: number,
}

@Component({
  selector: 'range-colorpicker',
  templateUrl: './range-colorpicker.component.html',
  styleUrls: ['./range-colorpicker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RangeColorpickerComponent implements AfterViewInit, OnChanges{

  @ViewChild('colorPan') colorPanel!: ElementRef;
  @ViewChild('knob') colorKnob!: ElementRef;

  @Input() disabled: boolean = false;
  @Input() currentColor: string = '';
  @Output() colorSelected: EventEmitter<string> = new EventEmitter();

  public selectedColor: string = '--knob: transparent';
  public selectedPosition: number = 0;
  public min: number = 0;
  public max: number = 300;

  private canvasContext!: CanvasRenderingContext2D;
  private isReadyToPick = false;

  constructor(private ref: ChangeDetectorRef) {}

  public ngAfterViewInit(): void {
    this.canvasContext = this.colorPanel.nativeElement.getContext('2d');
    const colorRangeWidth = this.canvasContext.canvas.width;
    this.canvasContext.fillStyle = this.prepareGradient(colorRangeWidth);
    this.canvasContext.fillRect(0, 0, colorRangeWidth, this.canvasContext.canvas.height);
    this.max = colorRangeWidth-1;

    this.isReadyToPick = true;

    // Position the knob for a color set before the canvas was ready, synchronously so
    // it never flashes at the blue default before landing (VIBU-36).
    if (this.currentColor) {
      this.setSelectedValue(this.currentColor);
      this.ref.detectChanges();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.isReadyToPick) {
      this.setSelectedValue(this.currentColor);
    }
  }

  public onStartMove() {
    this.selectedColor = `--knob: transparent`;
  }

  public onChangeValue(value: Event) {
    const selectedVal = (value as RangeCustomEvent).detail.value as number;
    const pixel = this.canvasContext.getImageData(selectedVal, 1, 1, 1)['data'];
    const rgb = {
      r: pixel[0], 
      g: pixel[1], 
      b: pixel[2]
    };
    const result = this.toHex(rgb);
    this.selectedColor = `--knob: ${result}`;
    this.colorSelected.emit(result);
  }

  private prepareGradient(colorRangeWidth: number): CanvasGradient {
    const gradient = this.canvasContext.createLinearGradient(0, 0, colorRangeWidth, 0);
    gradient.addColorStop(0, 'rgba(19,171,215,1)');
    gradient.addColorStop(0.05, 'rgba(19,171,215,1)');
    gradient.addColorStop(0.13, 'rgba(21,136,197,1)');
    gradient.addColorStop(0.20, 'rgba(123,83,151,1)');
    gradient.addColorStop(0.34, 'rgba(211,35,121,1)');
    gradient.addColorStop(0.48, 'rgba(223,23,77,1)');
    gradient.addColorStop(0.61, 'rgba(231,88,18,1)');
    gradient.addColorStop(0.70, 'rgba(240,140,14,1)');
    gradient.addColorStop(0.79, 'rgba(249,196,10,1)');
    gradient.addColorStop(0.87, 'rgba(188,192,17,1)');
    gradient.addColorStop(0.94, 'rgba(126,188,25,1)');
    return gradient;
  }

  private setSelectedValue(target: string) {
    const upper = target?.toUpperCase();
    // White is the circadian/default preset, not a hue on the strip — leave the knob put.
    if (!upper || upper === '#FFFFFF') {
      return;
    }

    const targetRgb = this.hexToRgb(upper);
    if (!targetRgb) {
      return;
    }

    // Move the knob to the gradient position CLOSEST to the target colour. Exact-hex
    // matching missed preset colours that aren't a verbatim gradient pixel, leaving the
    // knob where it was; nearest-match always lands on the right spot.
    // Read the whole strip in one getImageData: a call per pixel was slow enough on
    // device to leave the knob at blue for ~1s before landing (VIBU-36).
    const strip = this.canvasContext.getImageData(0, 1, this.max, 1).data;
    let bestPos = this.selectedPosition;
    let bestDist = Number.POSITIVE_INFINITY;
    for (let i = 0; i < this.max; i++) {
      const o = i * 4;
      const dr = strip[o] - targetRgb.r;
      const dg = strip[o + 1] - targetRgb.g;
      const db = strip[o + 2] - targetRgb.b;
      const dist = dr * dr + dg * dg + db * db;
      if (dist < bestDist) {
        bestDist = dist;
        bestPos = i;
        if (dist === 0) {
          break;
        }
      }
    }

    this.selectedPosition = bestPos;
    this.selectedColor = `--knob: ${upper}`;
  }

  private hexToRgb(hex: string): RgbValue | null {
    const clean = hex.replace('#', '');
    if (clean.length < 6) {
      return null;
    }
    const r = parseInt(clean.slice(0, 2), 16);
    const g = parseInt(clean.slice(2, 4), 16);
    const b = parseInt(clean.slice(4, 6), 16);
    if ([r, g, b].some((n) => Number.isNaN(n))) {
      return null;
    }
    return { r, g, b };
  }

  private toHex(rgbValue: RgbValue): string {
    return `#${this.componentToHex(rgbValue.r)}${this.componentToHex(rgbValue.g)}${this.componentToHex(rgbValue.b)}`.toUpperCase()
  }

  private componentToHex(c: number): string {
    var hex = c.toString(16);
    return (hex.length == 1 ? "0" + hex : hex)?.toUpperCase();
  }
}
